import React, { FunctionComponent, useCallback } from 'react';
import { Flex } from 'rebass/styled-components';
import { ReactComponent as IconUpArrow } from '../../assets/move-up-icon.svg';
import { ReactComponent as IconDownArrow } from '../../assets/move-down-icon.svg';
import api from '../../api';
import { useAppState, useNotification } from '../../hooks';
import { Loading } from '..';
import ButtonIcon from '../v2/button-icon';

export interface PolicyReorderProps {
  onReorder: (direction: string) => void;
  onReorderComplete: (
    success: boolean, topBoundryCondition: boolean, bottomBoundryCondition: boolean) => void;
  isReordering: string;
  selectedPolicy: any;
  overallSelectedPolicyOrder: number;
  totalPolicyCount: number;
  isFirstPolicyOnThePage: boolean;
  isLastPolicyOnThePage: boolean;
}

const PolicyReorder: FunctionComponent<PolicyReorderProps> = ({
  isReordering, onReorderComplete, onReorder, selectedPolicy,
  overallSelectedPolicyOrder, totalPolicyCount, isFirstPolicyOnThePage, isLastPolicyOnThePage,
}: PolicyReorderProps) => {
  const { accountId } = useAppState();
  const { addNotification } = useNotification();

  // overall order is offset from 1, not 0
  const isFirstPolicy: boolean = overallSelectedPolicyOrder === 1;
  const isLastPolicy: boolean = overallSelectedPolicyOrder === totalPolicyCount;

  const isUpArrowDisabled = isReordering || isFirstPolicy;
  const isDownArrowDisabled = isReordering || isLastPolicy;

  const swapError = useCallback(async (error) => {
    addNotification(error, 'error');
    onReorderComplete(false, false, false);
  }, [addNotification, onReorderComplete]);

  const move = useCallback(async (direction: string) => {
    if (!isReordering) {
      onReorder(direction);
      try {
        await api.ccsV2.reorderPolicy(accountId, selectedPolicy,
          overallSelectedPolicyOrder, direction);
        onReorderComplete(true, isFirstPolicyOnThePage && direction === 'up', isLastPolicyOnThePage && direction === 'down');
      } catch (err) {
        swapError(err);
      }
    }
  }, [isReordering, onReorder, accountId, selectedPolicy,
    overallSelectedPolicyOrder, swapError, onReorderComplete,
    isFirstPolicyOnThePage, isLastPolicyOnThePage]);

  return (
    <Flex width="80px" height="32px" justifyContent="space-between">
      <ButtonIcon
        testid={`order-up-button${isReordering === 'up' ? '-loading' : ''}`}
        disabled={isFirstPolicy}
        onClick={() => {
          if (!isUpArrowDisabled) {
            move('up');
          }
        }}
      >
        {isReordering === 'up' ? <Loading size={32} />
          : (
            <IconUpArrow width="32px" height="32px" />
          )}
      </ButtonIcon>
      <ButtonIcon
        testid={`order-down-button${isReordering === 'down' ? '-loading' : ''}`}
        disabled={isLastPolicy}
        onClick={() => {
          if (!isDownArrowDisabled) {
            move('down');
          }
        }}
      >
        {isReordering === 'down' ? <Loading size={32} />
          : (
            <IconDownArrow width="32px" height="32px" />
          )}
      </ButtonIcon>
    </Flex>
  );
};

export default PolicyReorder;
